// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect, useCallback } from 'react';
import { messaging, getToken, onMessage, isSupported } from '../config/firebase';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAPID_KEY = 'BKEEvbP_bXOwRSZuQBc8lTWSuS4P9zQMp6YV054ToAQ7u1wub7bVuBQ2z3HKrMGv4D32ZH0GuxfXvJPcC_bnE9Q';

export function useNotifications() {
  const [permission, setPermission] = useState(Notification.permission);
  const [fcmToken, setFcmToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    setSupported(isSupported());
  }, []);

  // Register service worker
  useEffect(() => {
    if (supported && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, [supported]);

  // Listen for foreground messages
  useEffect(() => {
    if (!messaging) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      
      // Show toast notification
      toast.info(payload.notification?.title || 'Nova notificação', {
        description: payload.notification?.body,
        action: payload.data?.url ? {
          label: 'Ver',
          onClick: () => window.location.href = payload.data.url
        } : undefined
      });
    });

    return () => unsubscribe();
  }, []);

  const requestPermission = useCallback(async () => {
    if (!supported) {
      toast.error('Seu navegador não suporta notificações');
      return false;
    }

    setLoading(true);
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        // Get FCM token
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: await navigator.serviceWorker.ready
        });
        
        if (token) {
          setFcmToken(token);
          
          // Save token to backend
          await saveTokenToBackend(token);
          
          toast.success('Notificações ativadas!');
          return true;
        }
      } else if (result === 'denied') {
        toast.error('Permissão de notificações negada');
      }
      
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Erro ao ativar notificações');
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const saveTokenToBackend = async (token) => {
    try {
      await axios.post(`${API}/notifications/register`, { token });
      console.log('FCM token saved to backend');
    } catch (error) {
      console.error('Error saving FCM token:', error);
    }
  };

  const unsubscribe = useCallback(async () => {
    if (fcmToken) {
      try {
        await axios.delete(`${API}/notifications/unregister`, {
          data: { token: fcmToken }
        });
        setFcmToken(null);
        toast.success('Notificações desativadas');
      } catch (error) {
        console.error('Error unsubscribing:', error);
      }
    }
  }, [fcmToken]);

  return {
    permission,
    fcmToken,
    loading,
    supported,
    requestPermission,
    unsubscribe,
    isEnabled: permission === 'granted' && !!fcmToken
  };
}
